/*
 * Revival "Complete your order" cart add-ons (snippets/revival-cart-addons.liquid).
 * Reads Shopify product recommendations for the items in the cart: complementary products first
 * (set in Search & Discovery), then related products as a fallback. One-click Add dispatches the
 * theme's CartLinesUpdateEvent so the cart drawer, cart page and cart count refresh.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
const cache = new Map();

function recs(id, intent) {
  const key = id + ':' + intent;
  if (!cache.has(key)) {
    cache.set(key, fetch(`${root}recommendations/products.json?product_id=${id}&limit=8&intent=${intent}`)
      .then((r) => (r.ok ? r.json() : { products: [] }))
      .then((d) => d.products || [])
      .catch(() => []));
  }
  return cache.get(key);
}

function money(cents) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: (Shopify.currency && Shopify.currency.active) || 'USD' }).format(cents / 100);
  } catch (e) { return '$' + (cents / 100).toFixed(2); }
}

function usable(p, inCart) {
  if (!p || !p.available || inCart.has(String(p.id))) return false;
  const tags = (p.tags || []).map((t) => String(t).toLowerCase());
  if (tags.includes('pharmacy') || tags.includes('rx')) return false;
  if (p.requires_selling_plan) return false;
  return true;
}

class RvCartAddons extends HTMLElement {
  static get observedAttributes() { return ['data-ids']; }

  connectedCallback() { this.schedule(); }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) this.schedule();
  }

  // The cart section is re-rendered (morphed) after every cart change, which can reset this element's
  // contents and hidden state. Re-render shortly after any change; recommendations are cached.
  schedule() {
    clearTimeout(this._t);
    this._t = setTimeout(() => this.load(), 60);
  }

  async load() {
    const ids = (this.dataset.ids || '').split(',').filter(Boolean).slice(0, 3);
    const inCart = new Set((this.dataset.inCart || '').split(',').filter(Boolean));
    const limit = Number(this.dataset.limit) || 4;
    const list = this.querySelector('[data-rv-addons-list]');
    if (!ids.length || !list) { this.hidden = true; return; }
    const key = ids.join(',') + '|' + [...inCart].join(',');
    if (key === this._key && list.children.length && !this.hidden) return;

    const picked = [];
    const seen = new Set();
    const add = (items) => {
      for (const p of items) {
        if (picked.length >= limit) return;
        if (seen.has(p.id) || !usable(p, inCart)) continue;
        seen.add(p.id);
        picked.push(p);
      }
    };
    const comp = await Promise.all(ids.map((id) => recs(id, 'complementary')));
    comp.forEach(add);
    if (picked.length < limit) add(await recs(ids[0], 'related'));
    if (!this.isConnected) return;
    if (!picked.length) { this.hidden = true; return; }

    list.innerHTML = '';
    for (const p of picked) list.appendChild(this.card(p));
    this.hidden = false;
    if (this._key !== key) {
      try {
        if (window.Shopify && Shopify.analytics) Shopify.analytics.publish('revival_cart_addons_shown', { products: picked.map((p) => p.id) });
      } catch (e) {}
    }
    this._key = key;
  }

  card(p) {
    const li = document.createElement('li');
    li.className = 'rv-addon';
    const img = p.featured_image ? (p.featured_image.startsWith('//') ? 'https:' + p.featured_image : p.featured_image) : '';
    const single = p.variants && p.variants.length === 1;
    const variant = (p.variants || []).find((v) => v.available) || (p.variants || [])[0];
    const sep = img.includes('?') ? '&' : '?';
    li.innerHTML =
      (img ? `<img class="rv-addon__img" src="${img}${sep}width=120" alt="" width="56" height="56" loading="lazy">` : '<span></span>') +
      `<div><a class="rv-addon__name" href="${p.url}"></a><span class="rv-addon__price">${money(p.price)}</span></div>` +
      (single && variant
        ? `<button type="button" class="rv-addon__btn">Add</button>`
        : `<a class="rv-addon__btn rv-addon__btn--alt" href="${p.url}">Options</a>`);
    li.querySelector('.rv-addon__name').textContent = p.title;
    const btn = li.querySelector('button.rv-addon__btn');
    if (btn) btn.addEventListener('click', () => this.addToCart(p, variant, btn));
    return li;
  }

  async addToCart(p, variant, btn) {
    btn.disabled = true;
    btn.textContent = 'Adding';
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variant.id), quantity: 1 }],
      promise: deferred.promise,
    }));
    try {
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
      });
      const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'revival-cart-addons', didError: !res.ok, productId: String(p.id) },
      });
      if (!res.ok) { btn.disabled = false; btn.textContent = 'Add'; return; }
      btn.textContent = 'Added';
      try {
        if (window.Shopify && Shopify.analytics) Shopify.analytics.publish('revival_cart_addon_added', { product_id: p.id, variant_id: variant.id });
        (window.dataLayer = window.dataLayer || []).push({ event: 'revival_cart_addon_added', product_id: p.id });
      } catch (e) {}
    } catch (e) {
      deferred.reject(e);
      btn.disabled = false;
      btn.textContent = 'Add';
    }
  }
}

if (!customElements.get('rv-cart-addons')) customElements.define('rv-cart-addons', RvCartAddons);

// After any cart change (add to cart, quantity change, remove), refresh every add-ons box once the
// cart drawer or cart page has re-rendered.
function refreshAll() {
  document.querySelectorAll('rv-cart-addons').forEach((el) => el.schedule());
}
document.addEventListener('shopify:cart:lines-update', (e) => {
  const p = e && (e.promise || (e.detail && e.detail.promise));
  if (p && typeof p.then === 'function') p.then(() => setTimeout(refreshAll, 250), () => {});
  setTimeout(refreshAll, 1200);
});
document.addEventListener('shopify:section:load', refreshAll);
